import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { PropertyUnitsService } from '../../services/property-units.service';
import { ActivatedRoute } from '@angular/router';
import { PROPERTY_RELATIONS_VIEWS } from 'src/app/shared/global/property-relations';
import { RelationOption } from '../../models/property-relations';
import { PropertyUnit } from 'src/app/models/interfaces/property-unit';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AlertBoxComponent } from 'src/app/shared/components/alert-box/alert-box.component';
import { forkJoin } from 'rxjs';
import { HttpEventType } from '@angular/common/http';
import { ConfirmationBoxComponent } from 'src/app/shared/components/confirmation-box/confirmation-box.component';
import { ExistingFile, FileType } from '../../models/property-files';
import { AddUnitFileComponent } from '../../components/add-unit-file/add-unit-file.component';
import { extractErrorMessage } from 'src/app/core/services/uitls/error.util';

@Component({
    selector: 'edit-property-unit',
    templateUrl: './edit-property-unit.component.html',
    styleUrls: ['./edit-property-unit.component.scss'],
    standalone: false
})
export class EditPropertyUnitComponent implements OnInit {
  unitId: string = '';
  submitted: boolean = false;
  errorMsg: boolean = false;
  message: string = '';
  saveDialogRef!: MatDialogRef<AlertBoxComponent>;
  confirmationDialogRef!: MatDialogRef<ConfirmationBoxComponent>;
  addFileDialogRef!: MatDialogRef<AddUnitFileComponent>;
  propertyUnitDetails: PropertyUnit | null = null;
  relationOptions: RelationOption[] = [];
  propertyFiles: any[] = [];
  fileError: string | null = null;

  propertyUnitFormGroup: FormGroup = new FormGroup({
    client_name: new FormControl(''),
    property_relations: new FormControl([]),
    custom_name: new FormControl<string | null>(null),
    building_type: new FormControl<number | null>(null),
    surfaces_space: new FormControl<string | null>(null),
    age: new FormControl<number | null>(null),
    location: new FormControl<string | null>(null),
    address_area: new FormControl<number | null>(null),
    address_city: new FormControl<number | null>(null),
    district: new FormControl<number | null>(null),
    address_street: new FormControl<string | null>(''),
    short_address: new FormControl<string | null>(''),
    address_building_code: new FormControl<string | null>(''),
    landmark: new FormControl<string | null>(''),
  });

  constructor(
    private propertyUnitService: PropertyUnitsService,
    private route: ActivatedRoute,
    private dialog: MatDialog,
  ) {}

  ngOnInit() {
    this.unitId = this.route.snapshot.paramMap.get('id') || '';
    this.getPropertyRelations();
    this.loadPropertyUnitDetails();
    this.getPropertyUnitFiles();

    this.propertyUnitFormGroup.get('short_address')?.clearValidators();
    this.propertyUnitFormGroup.get('short_address')?.setErrors(null);
    this.propertyUnitFormGroup.get('short_address')?.updateValueAndValidity();

    this.propertyUnitFormGroup.statusChanges.subscribe((status) => {
      if (status === 'VALID' && this.submitted) {
        this.errorMsg = false;
      }
    });
  }

  getPropertyRelations() {
    this.propertyUnitService
      .getPropertyRelations(PROPERTY_RELATIONS_VIEWS.UNIT)
      .subscribe((relations) => {
        this.relationOptions = relations.data || [];
      });
  }

  loadPropertyUnitDetails() {
    const mapResponse: boolean = false;
    this.propertyUnitService
      .getPropertyUnitDetails(this.unitId, mapResponse)
      .subscribe((unitDetails) => {
        this.propertyUnitDetails = unitDetails;
        this.propertyUnitFormGroup.patchValue({
          client_name: unitDetails.stakeholder?.name,
          property_relations: this.mapPropertyRelationsToFormValues(
            unitDetails.property_relations,
          ),
        });
      });
  }

  getPropertyUnitFiles() {
    this.fileError = null;
    forkJoin({
      fileTypes: this.propertyUnitService.getPropertyUnitFileTypes(),
      existingFiles: this.propertyUnitService.getPropertyUnitFiles(
        Number(this.unitId),
      ),
    }).subscribe({
      next: ({ fileTypes, existingFiles }) => {
        const fileTypesData: FileType[] = fileTypes.data || [];
        const existingFilesData: ExistingFile[] = existingFiles.data || [];

        // Map all file types with matched existing files
        const mappedFileTypes = fileTypesData.map((fileType: FileType) => {
          const existingFile =
            existingFilesData
              .filter(
                (file: ExistingFile) => file.file_type?.id === fileType.id,
              )
              .pop() || null;
          return {
            id: existingFile?.id || null,
            fileLabel: existingFile?.file || null,
            name: fileType.name || '',
            file_type_id: fileType.id || null,
            is_predefined: fileType.is_predefined || false,
            progress: false,
            progressVal: 0,
          };
        });

        // Find existing files that don't match any file type
        const otherFiles = existingFilesData
          .filter(
            (file: ExistingFile) =>
              !fileTypesData.some(
                (fileType: FileType) => fileType.id === file.file_type?.id,
              ),
          )
          .map((file: ExistingFile) => ({
            id: file.id || null,
            fileLabel: file.file || null,
            name: file.file_type?.name || '',
            file_type_id: file.file_type?.id || null,
            is_predefined: file.file_type?.is_predefined || false,
            progress: false,
            progressVal: 0,
          }));

        // Combine all file types and orphaned existing files
        this.propertyFiles = [...mappedFileTypes, ...otherFiles];
      },
      error: (error) => {
        this.propertyFiles = [];
        this.fileError = 'حدث خطأ أثناء تحميل الملفات';
      },
    });
  }

  mapPropertyRelationsToFormValues(relations: any[]): string[] {
    return relations.map((relation) => relation.id);
  }

  toggleRelation(value: string) {
    const currentValues =
      this.propertyUnitFormGroup.get('property_relations')?.value || [];
    const index = currentValues.indexOf(value);

    if (index > -1) {
      currentValues.splice(index, 1);
    } else {
      currentValues.push(value);
    }

    this.propertyUnitFormGroup.patchValue({
      property_relations: [...currentValues],
    });
  }

  isSelected(value: string): boolean {
    const currentValues =
      this.propertyUnitFormGroup.get('property_relations')?.value || [];
    return currentValues.includes(value);
  }

  closeError() {
    this.errorMsg = false;
  }

  handleFile(event: any, index: number, isPredefined: boolean) {
    if (isPredefined) {
      this.uploadFile(event, index);
    } else {
      this.updateFile(event, index);
    }
  }

  uploadFile(event: any, index: number) {
    const formData = new FormData();
    if (event.files && event.files.length > 0) {
      formData.append('file', event.files[0]);
      const fileTypeId = this.propertyFiles[index]?.file_type_id;
      formData.append('file_type_id', fileTypeId);
      this.propertyUnitService
        .uploadPropertyUnitFile(Number(this.unitId), formData)
        .subscribe((event) => {
          this.trackFileUploading(event, index, formData);
        });
    }
  }

  updateFile(event: any, index: number) {
    const formData = new FormData();
    if (event.files && event.files.length > 0) {
      formData.append('file', event.files[0]);
      const fileTypeId = this.propertyFiles[index]?.file_type_id;
      formData.append('file_type_id', fileTypeId);
      this.propertyUnitService
        .updatePropertyUnitFile(
          Number(this.unitId),
          Number(this.propertyFiles[index]?.id),
          formData,
        )
        .subscribe((event) => {
          this.trackFileUploading(event, index, formData);
        });
    }
  }

  trackFileUploading(event: any, index: number, formData: FormData) {
    switch (event.type) {
      case HttpEventType.Sent:
        this.propertyFiles[index].progress = false;
        break;
      case HttpEventType.UploadProgress:
        this.propertyFiles[index].progressVal = Math.round(
          (event.loaded / event.total) * 100,
        );
        this.propertyFiles[index].progress = true;
        break;
      case HttpEventType.Response:
        this.propertyFiles[index].progress = false;
        this.propertyFiles[index].file = formData.get('file') as File;
        this.propertyFiles[index]['fileLabel'] =
          event.body.data.file_type.name ||
          this.propertyFiles[index]?.name ||
          'file.pdf';
        this.propertyFiles[index]['is_predefined'] =
          event.body.data.file_type.is_predefined || false;
        setTimeout(() => {
          this.propertyFiles[index].progressVal = 0;
        }, 0);
    }
  }

  deleteFile(file: any, index: number) {
    if (file.id) {
      const confirmationData = {
        id: file.id,
        index: index,
        message: 'سيتم حذف الملف من النظام',
        callback: () => this.handleFileDelete(file, index),
      };
      this.showConfirmationBox(confirmationData);
    }
  }

  handleFileDelete(file: any, index: number) {
    this.propertyUnitService
      .deletePropertyUnitFile(Number(this.unitId), file.id)
      .subscribe({
        next: () => {
          if (this.propertyFiles[index].is_predefined) {
            this.propertyFiles[index].fileLabel = null;
            this.propertyFiles[index].file = null;
          } else {
            this.propertyFiles.splice(index, 1);
          }
        },
        error: (error) => {
          const errorMessage = extractErrorMessage(
            error,
            'حدث خطأ أثناء حذف الملف',
          );
          this.showAlertBox(false, errorMessage);
        },
      });
  }

  addAddFileInput() {
    this.addFileDialogRef = this.dialog.open(AddUnitFileComponent);
    this.addFileDialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        if (result.file && result.fileName) {
          const formData = new FormData();
          formData.append('file', result.file);
          formData.append('file_type_name', result.fileName);
          this.propertyUnitService
            .uploadPropertyUnitFile(Number(this.unitId), formData)
            .subscribe((event) => {
              if (event.type === HttpEventType.Response) {
                this.getPropertyUnitFiles();
              }
            });
        }
      }
    });
  }

  save(event: any) {
    this.submitted = true;
    this.errorMsg = false;

    // Clear any errors on short_address to allow form submission
    this.propertyUnitFormGroup.get('short_address')?.setErrors(null);

    if (this.propertyUnitFormGroup.invalid) {
      this.errorMsg = true;
      this.message = 'برجاء تعبئة  جميع الخانات ';
      this.propertyUnitFormGroup.markAllAsTouched();
      return;
    }
    const confirmationData = {
      id: 0,
      index: 0,
      title: 'سيتم اشعار العميل ',
      message: 'سيتم حفظ البيانات و اشعار العميل بالتغيير على الوحدة',
      confirmBtn: 'احفظ التعديل',
      isDelete: false,
      callback: () => this.updatePropertyUnit(event.buttonId),
    };
    this.showConfirmationBox(confirmationData);
  }

  updatePropertyUnit(buttonId: string) {
    const payload = this.propertyUnitFormGroup.value;
    this.propertyUnitService
      .updatePropertyUnit(this.unitId, payload, buttonId)
      .subscribe({
        next: (response) => {
          this.message = 'تم تحديث البيانات بنجاح';
          this.showAlertBox(true, this.message);
        },
        error: (error) => {
          const errorMessage = extractErrorMessage(
            error,
            'حدث خطأ أثناء تحديث البيانات',
          );
          this.showAlertBox(false, errorMessage);
        },
      });
  }

  showAlertBox(successImg: boolean, confirmMsg: string) {
    // Close any existing dialogs before opening a new one
    if (this.saveDialogRef) {
      this.saveDialogRef.close();
    }
    if (this.confirmationDialogRef) {
      this.confirmationDialogRef.close();
    }

    this.saveDialogRef = this.dialog.open(AlertBoxComponent, {
      data: {
        imgSuccess: successImg,
        message: confirmMsg,
        button: 'تم',
      },
    });
  }

  showConfirmationBox(confirmationData: any) {
    this.confirmationDialogRef = this.dialog.open(ConfirmationBoxComponent, {
      data: {
        id: confirmationData.id,
        index: confirmationData.index,
        title: confirmationData.title,
        message: confirmationData.message,
        confirmBtn: confirmationData.confirmBtn,
        isDelete: confirmationData.isDelete,
      },
    });

    this.confirmationDialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        confirmationData.callback();
      }
    });
  }
}
